import type { TOC } from '@ember/component/template-only';

export type CreditBalanceSignature = {
  Args: {
    /** Sessions of this kind the coachee has left to book. */
    remaining: number;
    /**
     * Singular name for what a credit buys, e.g. `chemistry call`. Pluralised
     * with a trailing "s", which is all the current nouns need.
     */
    noun: string;
  };
  Element: HTMLDivElement;
};

/**
 * Reads "2 chemistry calls remaining", turning red at zero.
 *
 * Module scope rather than a class field: it touches no component state, and a
 * plain function is what the template needs to invoke.
 */
function creditLabel(remaining: number, noun: string): string {
  if (remaining === 0) {
    return `No ${noun}s remaining`;
  }

  return `${remaining} ${noun}${remaining === 1 ? '' : 's'} remaining`;
}

/**
 * The coachee's remaining session credits, shown beside the places they spend
 * them. Extracted because the same card was written out at each of those places
 * and the copies had already drifted — one pluralised the noun, the other
 * dropped it and rendered a bare "1 remaining".
 */
export const CreditBalance: TOC<CreditBalanceSignature> = <template>
  <div
    class="ll-bg-raised border rounded-3 shadow-sm d-flex align-items-center gap-3 px-3 py-2 min-w-0 overflow-hidden"
    data-test-credit-balance
    ...attributes
  >
    <iconify-icon
      inline
      icon={{if @remaining "ph:credit-card" "ph:warning-circle"}}
      class="{{if @remaining 'text-primary' 'text-danger'}} fs-5 flex-shrink-0"
      aria-hidden="true"
    />
    <div class="lh-1 min-w-0">
      <p class="gud-eyebrow mb-0 text-body-secondary">Credit balance</p>
      {{! Zero is distinguished by text, icon, weight, and colour. }}
      <span
        class="fw-bold small text-break {{unless @remaining 'text-danger'}}"
      >{{creditLabel @remaining @noun}}</span>
    </div>
  </div>
</template>;

export default CreditBalance;
